import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";
import {
  FiArrowRight,
  FiBox,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiFileText,
  FiHome,
  FiPhone,
  FiImage,
  FiInfo,
  FiLayers,
  FiMail,
  FiMapPin,
  FiPackage,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUser,
  FiUserCheck,
  FiX,
} from "react-icons/fi";
import {
  MdDirectionsBoat,
  MdFlight,
  MdHeight,
  MdLocalShipping,
  MdMyLocation,
  MdOutlineWarehouse,
  MdScale,
  MdStraighten,
  MdSwapHoriz,
  MdTrain,
} from "react-icons/md";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { countryCodes, RoutesPaths } from "../Router/constant";
import { createOrder, createPublicOrder } from "../Services/bookingService";
import { getUserById } from "../Services/userService";
import { extractWarehouseList, getWarehouses } from "../Services/warehouseService";

const containerStyle = {
  width: "100%",
  height: "170px",
  borderRadius: "12px",
};

const logisticsTypeMap = {
  "Air freight": 1,
  "Sea freight": 2,
  "Rail freight": 3,
  Trucking: 4,
};

const MODES = [
  { value: "Air freight", hint: "Fast & Reliable", Icon: MdFlight },
  { value: "Sea freight", hint: "Cost Effective", Icon: MdDirectionsBoat },
  { value: "Rail freight", hint: "Stable & Efficient", Icon: MdTrain },
  { value: "Trucking", hint: "Flexible & Wide Coverage", Icon: MdLocalShipping },
];

const emptyWarehouseRequest = () => ({
  selectedWarehouse: "",
  numberOfDays: "",
  length: "",
  width: "",
  height: "",
});

// digits and one decimal point
const toDecimal = (value) => value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

const UNITS = ["cm", "mm", "inch", "meter", "feet"];

const libraries = ["places"];

const defaultCenter = {
  lat: 28.6139,
  lng: 77.209,
};

const emptyProduct = () => ({
  itemName: "",
  quantity: "",
  weight: "",
  height: "",
  heightUnit: "cm",
  width: "",
  widthUnit: "cm",
  previewImage: "",
  uploadedImage: "",
  uploading: false,
});

const emptyAddressDetails = () => ({
  house: "",
  city: "",
  zipcode: "",
});

const getDefaultCountry = () =>
  countryCodes.find((country) => country.code === "AO") ||
  countryCodes.find((country) => country.dial_code === "+244") ||
  countryCodes[0];

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const uploadSingleFile = async (payload) =>
  axios.post(`${process.env.REACT_APP_API_URL}/upload/uploadbase4`, payload);

/*
 * Shared "Create new order" form.
 *  variant="user"   -> order for an existing user (AddOrder, needs userId)
 *  variant="public" -> public order with account details (AddPublicOrder)
 */
export default function OrderForm({ variant = "user", userId }) {
  const isPublic = variant === "public";
  const navigate = useNavigate();

  const pickupAutoRef = useRef(null);
  const dropAutoRef = useRef(null);
  const countryRef = useRef(null);
  const shippingRef = useRef(null);
  const productsRef = useRef(null);
  const additionalRef = useRef(null);
  const submitRef = useRef(null);

  const [singleUserData, setSingleUserData] = useState(null);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [locating, setLocating] = useState(null);

  // Country
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(getDefaultCountry());

  // Form state
  const [selectedMode, setSelectedMode] = useState("Air freight");
  const [accountDetails, setAccountDetails] = useState({ name: "", email: "" });
  const [pickupDetails, setPickupDetails] = useState(emptyAddressDetails());
  const [dropDetails, setDropDetails] = useState(emptyAddressDetails());
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    accountType: "personal",
  });
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropLocation, setDropLocation] = useState(null);
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropAddress, setDropAddress] = useState("");
  const [formData, setFormData] = useState({ phoneNumber: "" });
  const [pickupDate, setPickupDate] = useState(null);
  const [products, setProducts] = useState([emptyProduct()]);
  const [requirements, setRequirements] = useState("");
  const [productChecked, setProductChecked] = useState(false);

  // Warehouse storage (sent with every order as `warehouse`)
  const [warehouseRequest, setWarehouseRequest] = useState(emptyWarehouseRequest());
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(true);

  // Load active warehouses for the dropdown.
  useEffect(() => {
    const load = async () => {
      setWarehousesLoading(true);
      try {
        const { list } = extractWarehouseList(
          await getWarehouses({ offset: 1, limit: 100, isActive: "true" }),
        );
        setWarehouses(list.filter((w) => w?.isActive !== false));
      } catch (error) {
        console.error("Load warehouses error:", error);
        toast.error("Unable to load warehouses");
      } finally {
        setWarehousesLoading(false);
      }
    };

    load();
  }, []);

  const selectedWarehouse = warehouses.find((w) => w._id === warehouseRequest.selectedWarehouse) || null;

  const updateWarehouseRequest = (key, value) =>
    setWarehouseRequest((prev) => ({ ...prev, [key]: value }));

  // Requested space larger than the selected warehouse's own dimensions?
  const oversizedDimensions = selectedWarehouse
    ? ["length", "width", "height"].filter((key) => {
        const requested = Number(warehouseRequest[key]);
        const available = Number(selectedWarehouse.dimensions?.[key]);
        return requested > 0 && available > 0 && requested > available;
      })
    : [];

  const filteredCountries = countryCodes.filter((country) => {
    const search = String(countrySearch || "").trim().toLowerCase();
    if (!search) return true;

    const countryName = String(
      country.name || country.country || country.label || "",
    ).toLowerCase();

    return (
      countryName.includes(search) ||
      String(country.code || "").toLowerCase().includes(search) ||
      String(country.dial_code || "").toLowerCase().includes(search)
    );
  });

  /*
   * USER DATA (user variant)
   */
  useEffect(() => {
    if (isPublic || !userId) return;

    const fetchUserData = async () => {
      try {
        const usersResp = await getUserById(userId);
        setSingleUserData(usersResp?.data?.data?.user);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    fetchUserData();
  }, [isPublic, userId]);

  /*
   * GOOGLE MAPS
   */
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBk6NKU8aUcbaQgqNJlGgh9ARAG6q5PkrA",
    libraries,
  });

  const reverseGeocode = async (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          resolve(results[0].formatted_address);
        } else {
          reject("No address found");
        }
      });
    });
  };

  // Default both maps to the browser's current location.
  useEffect(() => {
    if (!isLoaded) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        setPickupLocation(location);
        setDropLocation(location);

        try {
          const address = await reverseGeocode(location.lat, location.lng);
          setPickupAddress(address);
          setDropAddress(address);
        } catch (err) {
          console.warn(err);
        }
      },
      (error) => {
        console.warn("Geolocation denied or failed", error);
        setPickupLocation(defaultCenter);
        setDropLocation(defaultCenter);
      },
    );
  }, [isLoaded]);

  /*
   * COUNTRY OUTSIDE CLICK
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setIsCountryOpen(false);
        setCountrySearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsCountryOpen(false);
    setCountrySearch("");
  };

  /*
   * RESET
   */
  const resetForm = () => {
    setPickupLocation(null);
    setDropLocation(null);
    setPickupAddress("");
    setDropAddress("");
    setPickupDetails(emptyAddressDetails());
    setDropDetails(emptyAddressDetails());
    setFormData({ phoneNumber: "" });
    setUserDetails({ name: "", email: "", accountType: "personal" });
    setAccountDetails({ name: "", email: "" });
    setSelectedCountry(getDefaultCountry());
    setCountrySearch("");
    setIsCountryOpen(false);
    setProducts([emptyProduct()]);
    setPickupDate(null);
    setRequirements("");
    setProductChecked(false);
    setWarehouseRequest(emptyWarehouseRequest());
  };

  /*
   * PRODUCTS
   */
  const updateProduct = (index, changes) =>
    setProducts((prev) =>
      prev.map((product, i) => (i === index ? { ...product, ...changes } : product)),
    );

  const handleProductChange = (index, field, value) =>
    updateProduct(index, { [field]: value });

  const addProduct = () => setProducts((prev) => [...prev, emptyProduct()]);

  const removeProduct = (index) =>
    setProducts((prev) => prev.filter((_, i) => i !== index));

  const handleRemoveImage = (index) =>
    updateProduct(index, { previewImage: "", uploadedImage: "" });

  const handleFileChange = async (e, index) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) return;

    try {
      updateProduct(index, { uploading: true });

      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await uploadSingleFile({ base64 });

      updateProduct(index, {
        previewImage: base64,
        uploadedImage: response?.data?.data?.fileName || "",
        uploading: false,
      });
    } catch (err) {
      console.error(err);
      updateProduct(index, { uploading: false });
    }
  };

  /*
   * PHONE INPUT
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === "phoneNumber" ? value.replace(/\D/g, "") : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
  };

  /*
   * CURRENT LOCATION (per address card)
   */
  const locateCurrentPosition = (side) => {
    if (!navigator.geolocation) {
      toast.error("Location is not supported by this browser");
      return;
    }

    const setLocation = side === "pickup" ? setPickupLocation : setDropLocation;
    const setAddress = side === "pickup" ? setPickupAddress : setDropAddress;

    setLocating(side);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLocation({ lat, lng });

        try {
          setAddress(await reverseGeocode(lat, lng));
        } catch (err) {
          console.warn(err);
        } finally {
          setLocating(null);
        }
      },
      () => {
        setLocating(null);
        toast.error("Unable to get your current location");
      },
    );
  };

  /*
   * SUBMIT
   */
  const handleSubmit = async () => {
    if (isPublic && (!accountDetails.name || !accountDetails.email)) {
      toast.error("Please fill account details");
      return;
    }

    if (!pickupLocation || !pickupAddress) {
      toast.error("Please select pickup location");
      return;
    }

    if (!dropLocation || !dropAddress) {
      toast.error("Please select drop location");
      return;
    }

    if (!userDetails.name) {
      toast.error("Please enter name");
      return;
    }

    if (!formData.phoneNumber) {
      toast.error("Please enter phone number");
      return;
    }

    if (!userDetails.email) {
      toast.error("Please enter email");
      return;
    }

    if (!validateEmail(userDetails.email)) {
      toast.error("Enter valid email");
      return;
    }

    if (!pickupDate) {
      toast.error("Please select pickup date");
      return;
    }

    // Warehouse storage (required on every order)
    {
      const days = Number(warehouseRequest.numberOfDays);

      if (!warehouseRequest.selectedWarehouse) {
        toast.error("Please select a warehouse");
        return;
      }

      if (!warehouseRequest.numberOfDays || !Number.isInteger(days) || days <= 0) {
        toast.error("Please enter the number of storage days");
        return;
      }

      const missingDimension = ["length", "width", "height"].find(
        (key) => !(Number(warehouseRequest[key]) > 0),
      );
      if (missingDimension) {
        toast.error(`Please enter the storage ${missingDimension}`);
        return;
      }

      if (oversizedDimensions.length > 0) {
        toast.error(
          `Requested ${oversizedDimensions.join(", ")} is larger than ${selectedWarehouse?.name || "the warehouse"}`,
        );
        return;
      }
    }

    if (!productChecked) {
      toast.error("Please confirm product details checkbox");
      return;
    }

    if (products.length === 0) {
      toast.error("Add at least one product");
      return;
    }

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      const quantity = Number(product.quantity);
      const weight = Number(product.weight);

      if (!product.itemName.trim() || !product.quantity || !product.weight) {
        toast.error(`Fill all product fields (${i + 1})`);
        return;
      }

      if (!product.uploadedImage) {
        toast.error(`Please upload image for product (${i + 1})`);
        return;
      }

      if (isNaN(quantity) || isNaN(weight)) {
        toast.error(`Quantity & Weight must be numbers (${i + 1})`);
        return;
      }

      if (quantity <= 0 || weight <= 0) {
        toast.error(`Quantity & Weight must be greater than 0 (${i + 1})`);
        return;
      }
    }

    const formattedDate = pickupDate ? pickupDate.toISOString().split("T")[0] : null;

    const payload = {
      ...(isPublic
        ? { accountDetails: { name: accountDetails.name, email: accountDetails.email } }
        : { userId }),

      logisticsType: logisticsTypeMap[selectedMode] || 1,

      pickupAddress: {
        address: pickupAddress,
        pickupHousenoStno: pickupDetails.house,
        pickupCity: pickupDetails.city,
        pickupZipcode: pickupDetails.zipcode,
        longitude: pickupLocation?.lng || "",
        latitude: pickupLocation?.lat || "",
      },

      dropAddress: {
        address: dropAddress,
        dropHousenoStno: dropDetails.house,
        dropCity: dropDetails.city,
        dropZipcode: dropDetails.zipcode,
        longitude: dropLocation?.lng || "",
        latitude: dropLocation?.lat || "",
      },

      // Default AO = +244
      countryCode: selectedCountry?.dial_code || "+244",
      phoneNumber: formData.phoneNumber,
      name: userDetails.name,
      accountType: userDetails.accountType,
      email: userDetails.email,
      estimatedCharges: "100",

      productDetails: products.map((product) => ({
        name: product.itemName,
        quantity: product.quantity,
        weight: product.weight,
        width: product.width,
        widthOption: product.widthUnit,
        height: product.height,
        heightOption: product.heightUnit,
        image: product.uploadedImage,
      })),

      // Warehouse storage request
      warehouse: {
        numberOfDays: String(Number(warehouseRequest.numberOfDays)),
        dimensions: {
          height: Number(warehouseRequest.height),
          length: Number(warehouseRequest.length),
          width: Number(warehouseRequest.width),
        },
        selectedWarehouse: warehouseRequest.selectedWarehouse,
      },

      description: requirements.trim(),
      currency: "AOA",
      questions: [],
      signedDocument: "",
      alternatePhone: "",
      pickupTime: "",
      date: formattedDate,
    };

    try {
      setSubmitLoader(true);

      if (isPublic) {
        const response = await createPublicOrder(payload);

        if (response?.data?.success) {
          toast.success("Order created successfully");
          resetForm();
          navigate("/booking-management");
        } else {
          toast.error(response?.data?.message || "Failed to create order");
        }
      } else {
        const response = await createOrder(payload);

        if (response?.status === 200 && response?.data?.status) {
          toast.success("Order created successfully");
          resetForm();
          navigate("/booking-management");
        } else {
          toast.error("Submission failed");
        }
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(
        isPublic
          ? error?.response?.data?.message || "Something went wrong"
          : "Something went wrong",
      );
    } finally {
      setSubmitLoader(false);
    }
  };

  /*
   * STEP PROGRESS (visual only — the form is a single page)
   */
  const shippingDone = Boolean(
    (!isPublic || (accountDetails.name && accountDetails.email)) &&
      pickupLocation &&
      pickupAddress &&
      dropLocation &&
      dropAddress &&
      userDetails.name &&
      formData.phoneNumber &&
      validateEmail(userDetails.email) &&
      pickupDate,
  );

  const productsDone =
    products.length > 0 &&
    products.every(
      (p) =>
        p.itemName.trim() &&
        Number(p.quantity) > 0 &&
        Number(p.weight) > 0 &&
        p.uploadedImage,
    );

  const steps = [
    { title: "Shipping Details", hint: "Pickup & drop location", done: shippingDone, ref: shippingRef },
    { title: "Product Details", hint: "Items & dimensions", done: productsDone, ref: productsRef },
    { title: "Additional Information", hint: "Your requirements", done: productChecked, ref: additionalRef },
    { title: "Review & Submit", hint: "Confirm and submit", done: false, ref: submitRef },
  ];
  const activeStep = steps.findIndex((s) => !s.done);

  const scrollTo = (ref) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const userDisplayName = singleUserData ? `${singleUserData.fullName || ""}`.trim() : "";

  // Section numbering shifts by one when the account block is shown.
  // Account (public) and Warehouse sections slot in after section 1.
  const warehouseSectionNo = isPublic ? 3 : 2;
  const sectionNo = (n) => (n > 1 ? n + (isPublic ? 1 : 0) + 1 : n);

  /*
   * RENDER HELPERS
   */
  const renderAddressCard = ({
    side,
    title,
    Icon,
    location,
    setLocation,
    address,
    setAddress,
    details,
    setDetails,
    autoRef,
    placeholder,
  }) => {
    const handleMapPoint = async (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setLocation({ lat, lng });

      try {
        setAddress(await reverseGeocode(lat, lng));
      } catch (err) {
        console.warn(err);
      }
    };

    return (
      <div className="qo-address-card">
        <div className="qo-address-head">
          <span className="qo-address-title">
            <Icon className="qo-accent" />
            {title} *
          </span>
          <button
            type="button"
            className="qd-btn qd-btn-outline qd-btn-sm"
            onClick={() => locateCurrentPosition(side)}
            disabled={!isLoaded || locating === side}
          >
            <MdMyLocation className="qo-accent" />
            {locating === side ? "Locating..." : "Use current location"}
          </button>
        </div>

        <div className="qo-map">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={location || defaultCenter}
              zoom={14}
              onClick={handleMapPoint}
            >
              {location && (
                <Marker position={location} draggable onDragEnd={handleMapPoint} />
              )}
            </GoogleMap>
          ) : (
            <div className="qo-map-loading">Loading map...</div>
          )}
        </div>

        {isLoaded && (
          <Autocomplete
            onLoad={(ref) => (autoRef.current = ref)}
            onPlaceChanged={() => {
              const place = autoRef.current.getPlace();
              if (!place.geometry) return;

              setLocation({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
              });
              setAddress(place.formatted_address || "");
            }}
          >
            <Field id={`qo-${side}-address`} Icon={FiMapPin} label={side === "pickup" ? "Pickup location" : "Drop location"} required>
              <input
                id={`qo-${side}-address`}
                type="text"
                placeholder={placeholder}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Field>
          </Autocomplete>
        )}

        <Field id={`qo-${side}-details`} Icon={FiHome} label="Address details">
          <input
            id={`qo-${side}-details`}
            placeholder="Building, floor, landmark (optional)"
            value={details.house}
            onChange={(e) => setDetails({ ...details, house: e.target.value })}
          />
        </Field>
      </div>
    );
  };

  const renderUnitField = (index, product, field, unitField, label, Icon) => (
    <Field
      id={`qo-${field}-${index}`}
      Icon={Icon}
      label={label}
      addon={
        <select
          className="qo-unit"
          value={product[unitField]}
          onChange={(e) => handleProductChange(index, unitField, e.target.value)}
          aria-label={`${label} unit`}
        >
          {UNITS.map((unit) => (
            <option key={unit} value={unit}>
              {unit}
            </option>
          ))}
        </select>
      }
    >
      <input
        id={`qo-${field}-${index}`}
        placeholder={`Enter ${label.toLowerCase()}`}
        inputMode="numeric"
        value={product[field]}
        onChange={(e) =>
          handleProductChange(index, field, e.target.value.replace(/\D/g, ""))
        }
      />
    </Field>
  );

  return (
    <div className="container-fluid qd-page qo-page">
      {/* Breadcrumb */}
      <nav className="qd-breadcrumb">
        <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
          <FiHome />
        </NavLink>
        <FiChevronRight />
        {isPublic ? (
          <NavLink to={RoutesPaths.BOOKINGMANAGEMENT}>Booking management</NavLink>
        ) : (
          <NavLink to={RoutesPaths.USERMANAGEMENT}>User management</NavLink>
        )}
        <FiChevronRight />
        <span className="qd-breadcrumb-current">Create new order</span>
      </nav>

      {/* Hero */}
      <div
        className="qo-hero"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/img/LogisticsRoute.png)` }}
      >
        <h1 className="qd-title">Create New Order</h1>
        <p className="qd-subtitle">
          Fill in the details below to get a quick quotation for your shipment.
        </p>
        {userDisplayName && (
          <span className="qo-for-chip">
            <FiUserCheck /> Creating order for <strong>{userDisplayName}</strong>
          </span>
        )}
      </div>

      {/* Stepper */}
      {/* <div className="qd-card qo-stepper">
        {steps.map((step, i) => {
          const state = step.done ? "done" : i === activeStep ? "active" : "";
          return (
            <React.Fragment key={step.title}>
              {i > 0 && <span className={`qo-step-line ${steps[i - 1].done ? "done" : ""}`} />}
              <button type="button" className={`qo-step ${state}`} onClick={() => scrollTo(step.ref)}>
                <span className="qo-step-num">{step.done ? <FiCheck /> : i + 1}</span>
                <span className="qo-step-text">
                  <strong>{step.title}</strong>
                  <small>{step.hint}</small>
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div> */}

      {/* 1. Logistic type */}
      <section className="qd-card qo-section" ref={shippingRef}>
        <SectionHeader
          Icon={FiBox}
          title="1. Select Logistic Type"
          hint="Choose the type of logistics service for your shipment."
        />
        <div className="qo-mode-grid">
          {MODES.map(({ value, hint, Icon }) => {
            const selected = selectedMode === value;
            return (
              <button
                type="button"
                key={value}
                className={`qo-mode ${selected ? "selected" : ""}`}
                onClick={() => setSelectedMode(value)}
                aria-pressed={selected}
              >
                <span className="qo-mode-icon">
                  <Icon />
                </span>
                <span className="qo-mode-text">
                  <strong>{value}</strong>
                  <small>{hint}</small>
                </span>
                {selected && (
                  <span className="qo-check">
                    <FiCheck />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Account details (public only) */}
      {isPublic && (
        <section className="qd-card qo-section">
          <SectionHeader
            Icon={FiUserCheck}
            title="2. Account Details"
            hint="The account this order will be created under."
          />
          <div className="qo-grid-2">
            <Field id="qo-account-name" Icon={FiUser} label="Account name" required>
              <input
                id="qo-account-name"
                placeholder="Enter name"
                value={accountDetails.name}
                onChange={(e) => setAccountDetails({ ...accountDetails, name: e.target.value })}
              />
            </Field>
            <Field id="qo-account-email" Icon={FiMail} label="Account email" required>
              <input
                id="qo-account-email"
                type="email"
                placeholder="Enter email"
                value={accountDetails.email}
                onChange={(e) => setAccountDetails({ ...accountDetails, email: e.target.value })}
              />
            </Field>
          </div>
        </section>
      )}

      {/* Warehouse storage */}
      <section className="qd-card qo-section qo-warehouse">
        <SectionHeader
          Icon={MdOutlineWarehouse}
          title={`${warehouseSectionNo}. Warehouse Storage`}
          hint="Choose a warehouse, how long to store the goods and the space you need."
        />

        <div className="qo-grid-2">
          <Field
            id="qo-wh-select"
            Icon={MdOutlineWarehouse}
            label="Select warehouse"
            required
            className="qo-wh-select-field"
          >
            <select
              id="qo-wh-select"
              className="qo-wh-select"
              value={warehouseRequest.selectedWarehouse}
              onChange={(e) => updateWarehouseRequest("selectedWarehouse", e.target.value)}
              disabled={warehousesLoading || warehouses.length === 0}
            >
              <option value="">
                {warehousesLoading
                  ? "Loading warehouses..."
                  : warehouses.length === 0
                    ? "No active warehouses"
                    : "Select a warehouse"}
              </option>
              {warehouses.map((warehouse) => (
                <option key={warehouse._id} value={warehouse._id}>
                  {warehouse.name || "Untitled warehouse"}
                  {warehouse.location ? ` — ${warehouse.location}` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field id="qo-wh-days" Icon={FiCalendar} label="Number of days" required>
            <input
              id="qo-wh-days"
              inputMode="numeric"
              placeholder="e.g. 13"
              value={warehouseRequest.numberOfDays}
              onChange={(e) => updateWarehouseRequest("numberOfDays", e.target.value.replace(/\D/g, ""))}
            />
          </Field>
        </div>

        {!warehousesLoading && warehouses.length === 0 && (
          <p className="qo-wh-hint">
            No active warehouses yet.{" "}
            <NavLink to={RoutesPaths.ADDWAREHOUSE}>Create a warehouse</NavLink> to use it here.
          </p>
        )}

        <div className="qo-wh-dims">
          {[
            { key: "length", label: "Length (m)", Icon: MdStraighten, placeholder: "e.g. 7" },
            { key: "width", label: "Width (m)", Icon: MdSwapHoriz, placeholder: "e.g. 10" },
            { key: "height", label: "Height (m)", Icon: MdHeight, placeholder: "e.g. 10" },
          ].map(({ key, label, Icon, placeholder }) => (
            <Field
              key={key}
              id={`qo-wh-${key}`}
              Icon={Icon}
              label={label}
              required
              className={oversizedDimensions.includes(key) ? "has-error" : ""}
            >
              <input
                id={`qo-wh-${key}`}
                inputMode="decimal"
                placeholder={placeholder}
                value={warehouseRequest[key]}
                onChange={(e) => updateWarehouseRequest(key, toDecimal(e.target.value))}
              />
            </Field>
          ))}
        </div>

        {selectedWarehouse && (
          <div className="qo-wh-notes">
            <p className={oversizedDimensions.length ? "is-error" : ""}>
              <FiInfo />
              <span>
                {selectedWarehouse.dimensions
                  ? oversizedDimensions.length
                    ? `Requested ${oversizedDimensions.join(", ")} is larger than ${selectedWarehouse.name} (${selectedWarehouse.dimensions.length} × ${selectedWarehouse.dimensions.width} × ${selectedWarehouse.dimensions.height} m).`
                    : `${selectedWarehouse.name}: ${selectedWarehouse.dimensions.length} × ${selectedWarehouse.dimensions.width} × ${selectedWarehouse.dimensions.height} m${selectedWarehouse.location ? ` · ${selectedWarehouse.location}` : ""}.`
                  : `${selectedWarehouse.name}${selectedWarehouse.location ? ` · ${selectedWarehouse.location}` : ""}.`}
                {Number(warehouseRequest.length) > 0 &&
                  Number(warehouseRequest.width) > 0 &&
                  Number(warehouseRequest.height) > 0 && (
                    <>
                      {" "}Requested space:{" "}
                      <strong>
                        {(
                          Number(warehouseRequest.length) *
                          Number(warehouseRequest.width) *
                          Number(warehouseRequest.height)
                        ).toLocaleString()}{" "}
                        m³
                      </strong>
                      {Number(warehouseRequest.numberOfDays) > 0 && (
                        <>
                          {" "}for{" "}
                          <strong>
                            {Number(warehouseRequest.numberOfDays)} day
                            {Number(warehouseRequest.numberOfDays) === 1 ? "" : "s"}
                          </strong>
                        </>
                      )}
                      .
                    </>
                  )}
              </span>
            </p>
          </div>
        )}
      </section>

      {/* 2. Pickup & drop */}
      <section className="qd-card qo-section">
        <SectionHeader
          Icon={FiMapPin}
          title={`${sectionNo(2)}. Pickup & Drop Address`}
          hint="Enter pickup and drop locations for your shipment."
        />

        <div className="qo-grid-2">
          {renderAddressCard({
            side: "pickup",
            title: "Pickup address",
            Icon: FiPackage,
            location: pickupLocation,
            setLocation: setPickupLocation,
            address: pickupAddress,
            setAddress: setPickupAddress,
            details: pickupDetails,
            setDetails: setPickupDetails,
            autoRef: pickupAutoRef,
            placeholder: "Search pickup location",
          })}
          {renderAddressCard({
            side: "drop",
            title: "Drop address",
            Icon: FiMapPin,
            location: dropLocation,
            setLocation: setDropLocation,
            address: dropAddress,
            setAddress: setDropAddress,
            details: dropDetails,
            setDetails: setDropDetails,
            autoRef: dropAutoRef,
            placeholder: "Search drop location",
          })}
        </div>

        <div className="qo-grid-2 qo-contact">
          {/* Phone + searchable country */}
          <Field id="qo-phone" Icon={FiPhone} label="Phone number" required className="qo-phone">
          <div className="qo-phone-row">
            <div className="qo-country" ref={countryRef}>
              <button
                type="button"
                className="qo-country-trigger"
                onClick={() => {
                  setIsCountryOpen((prev) => !prev);
                  if (isCountryOpen) setCountrySearch("");
                }}
              >
                <img
                  src={`https://flagcdn.com/24x18/${selectedCountry.code.toLowerCase()}.png`}
                  alt={selectedCountry.code}
                  className="qo-flag"
                />
                <span>
                  {selectedCountry.dial_code} ({selectedCountry.code})
                </span>
                <FiChevronDown className={`qo-chevron ${isCountryOpen ? "open" : ""}`} />
              </button>

              {isCountryOpen && (
                <div className="qo-country-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="qo-country-search">
                    <FiSearch />
                    <input
                      type="text"
                      value={countrySearch}
                      autoFocus
                      placeholder="Search country or code"
                      onChange={(e) => setCountrySearch(e.target.value)}
                    />
                    {countrySearch && (
                      <button type="button" onClick={() => setCountrySearch("")} aria-label="Clear">
                        <FiX />
                      </button>
                    )}
                  </div>

                  <div className="qo-country-list">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => {
                        const isSelected = selectedCountry?.code === country.code;
                        const name = country.name || country.country || country.label;
                        return (
                          <div
                            key={`${country.code}-${country.dial_code}`}
                            className={`qo-country-item ${isSelected ? "selected" : ""}`}
                            onClick={() => handleCountrySelect(country)}
                          >
                            <img
                              src={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
                              alt={country.code}
                              className="qo-flag"
                            />
                            <span className="qo-country-info">
                              {name && <span>{name}</span>}
                              <small>
                                {country.dial_code} ({country.code})
                              </small>
                            </span>
                            {isSelected && <FiCheck className="qo-accent" />}
                          </div>
                        );
                      })
                    ) : (
                      <div className="qo-country-empty">No country found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <input
              id="qo-phone"
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Enter phone number"
              inputMode="numeric"
            />
          </div>
          </Field>

          <Field id="qo-name" Icon={FiUser} label="Name" required>
            <input
              id="qo-name"
              placeholder="Enter full name"
              value={userDetails.name}
              onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
            />
          </Field>

          <Field id="qo-date" Icon={FiCalendar} label="Pickup date" required className="qo-date">
            <DatePicker
              id="qo-date"
              selected={pickupDate}
              onChange={(date) => setPickupDate(date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select pickup date"
              minDate={new Date()}
              customInput={<input readOnly />}
            />
          </Field>

          <Field id="qo-email" Icon={FiMail} label="Email" required>
            <input
              id="qo-email"
              type="email"
              placeholder="Enter email address"
              value={userDetails.email}
              onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
            />
          </Field>
        </div>

        <p className="qo-label">What describes you best? *</p>
        <div className="qo-account-types">
          {[
            { value: "personal", label: "Personal user", Icon: FiUser },
            { value: "business", label: "Business user", Icon: FiBriefcase },
          ].map(({ value, label, Icon }) => {
            const selected = userDetails.accountType === value;
            return (
              <button
                type="button"
                key={value}
                className={`qo-account-type ${selected ? "selected" : ""}`}
                onClick={() => setUserDetails({ ...userDetails, accountType: value })}
                aria-pressed={selected}
              >
                <Icon className="qo-account-icon" />
                {label}
                {selected && (
                  <span className="qo-check qo-check-inline">
                    <FiCheck />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* 3. Products */}
      <section className="qd-card qo-section" ref={productsRef}>
        <SectionHeader
          Icon={FiBox}
          title={`${sectionNo(3)}. Product attributes`}
          hint="You can fill in the following product attributes to get a quotation within 24 hours."
          action={
            <button type="button" className="qo-soft-btn" onClick={addProduct}>
              <FiPlus /> Add more product
            </button>
          }
        />

        {products.map((product, index) => (
          <div className="qo-product" key={index}>
            {products.length > 1 && (
              <div className="qo-product-head">
                <span>Product {index + 1}</span>
                <button
                  type="button"
                  className="qo-remove"
                  onClick={() => removeProduct(index)}
                >
                  <FiTrash2 /> Remove
                </button>
              </div>
            )}

            <div className="qo-product-body">
              <div
                className={`qo-upload ${product.previewImage ? "has-image" : ""}`}
                onClick={() => {
                  if (!product.uploading) {
                    document.getElementById(`file-${index}`)?.click();
                  }
                }}
              >
                {product.previewImage ? (
                  <img src={product.previewImage} alt="Product" />
                ) : (
                  <>
                    <FiImage className="qo-upload-icon" />
                    <strong>Upload product image *</strong>
                    <small>JPG, PNG (Max 5MB)</small>
                  </>
                )}

                {product.uploading && <div className="qo-upload-overlay">Uploading...</div>}

                {product.previewImage && !product.uploading && (
                  <button
                    type="button"
                    className="qo-upload-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage(index);
                    }}
                    aria-label="Remove image"
                  >
                    <FiX />
                  </button>
                )}
              </div>

              <input
                type="file"
                id={`file-${index}`}
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e, index)}
              />

              <div className="qo-product-fields">
                <Field id={`qo-item-${index}`} Icon={FiPackage} label="Item name" required>
                  <input
                    id={`qo-item-${index}`}
                    placeholder="Enter item name"
                    value={product.itemName}
                    onChange={(e) => handleProductChange(index, "itemName", e.target.value)}
                  />
                </Field>

                <Field id={`qo-qty-${index}`} Icon={FiLayers} label="Quantity" required>
                  <input
                    id={`qo-qty-${index}`}
                    placeholder="Enter quantity"
                    inputMode="numeric"
                    value={product.quantity}
                    onChange={(e) =>
                      handleProductChange(index, "quantity", e.target.value.replace(/\D/g, ""))
                    }
                  />
                </Field>

                <Field id={`qo-weight-${index}`} Icon={MdScale} label="Weight (kg)" required>
                  <input
                    id={`qo-weight-${index}`}
                    placeholder="Enter weight"
                    inputMode="decimal"
                    value={product.weight}
                    onChange={(e) =>
                      handleProductChange(index, "weight", e.target.value.replace(/[^0-9.]/g, ""))
                    }
                  />
                </Field>

                {renderUnitField(index, product, "height", "heightUnit", "Height", MdHeight)}
                {renderUnitField(index, product, "width", "widthUnit", "Width", MdSwapHoriz)}
              </div>
            </div>
          </div>
        ))}

        {/* Additional information */}
        <div ref={additionalRef} className="qo-requirements">
          <p className="qo-label qo-label-icon">
            <FiFileText className="qo-accent" /> Detailed requirements
          </p>
          <textarea
            className="qo-textarea"
            placeholder="Please type in your detailed requirements (optional)..."
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
          />
        </div>

        <div className="qo-footer" ref={submitRef}>
          <label className="qo-confirm">
            <input
              type="checkbox"
              checked={productChecked}
              onChange={(e) => setProductChecked(e.target.checked)}
            />
            I hereby confirm that the product details provided are accurate and correct.
          </label>

          <button
            type="button"
            className="qd-btn qd-btn-primary qo-submit"
            onClick={handleSubmit}
            disabled={submitLoader}
          >
            {submitLoader ? (
              <>
                <span className="qo-spinner" /> Submitting...
              </>
            ) : (
              <>
                Submit <FiArrowRight />
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

/*
 * The one input style used across the order form:
 * icon tile | small label + control | optional addon (e.g. a unit select).
 */
function Field({ id, Icon, label, required = false, addon, className = "", children }) {
  return (
    <div className={`qo-field ${className}`}>
      <span className="qo-field-icon" aria-hidden="true">
        <Icon />
      </span>
      <div className="qo-field-body">
        <label htmlFor={id}>
          {label}
          {required && <span className="qo-required"> *</span>}
        </label>
        {children}
      </div>
      {addon}
    </div>
  );
}

function SectionHeader({ Icon, title, hint, action }) {
  return (
    <div className="qo-section-head">
      <div className="qd-panel-title">
        <span className="qd-panel-icon">
          <Icon />
        </span>
        <div>
          <h6>{title}</h6>
          <p className="qd-panel-sub">{hint}</p>
        </div>
      </div>
      {action}
    </div>
  );
}
